import React, { useId, useState } from 'react';
import moment from 'moment';
import { useMediaQuery } from 'usehooks-ts';
import { LuCalendarDays, LuChevronRight, LuX } from 'react-icons/lu';
import { Modal } from '@/components/Modal';
import { Drawer } from '@/components/Drawer';
import { useHospitalityReservationList } from '@/hooks/useHospitalityReservationList';
import type { HospitalityReservationListItemDTO } from '@/services/hospitality.service';
import {
    getOwnerReservationWaveSummaryCounts,
    isOwnerReservationWavesFullyScheduled,
    OWNER_RESERVATION_WAVES_LIMIT_PER_DAY,
} from '@/helpers/ownerReservationWaves';
import {
    CenteredState,
    DrawerHeader,
    DrawerRetryButton,
    ModalRetryButton,
    PanelScroll,
    ReservationAccessLink,
    ReservationCard,
    ReservationSummaryToggle,
    ReservationSummaryToggleWrap,
    ReservationWavesAllScheduled,
    ReservationWavesSection,
    Spinner,
    VerReservasHeaderButton,
} from './styles';

type OwnerReservationsPanelProps = {
    open: boolean;
    onClose: () => void;
};

function OwnerReservationListCard({
    item,
    surface,
    onClose,
}: {
    item: HospitalityReservationListItemDTO;
    surface: 'light' | 'dark';
    onClose: () => void;
}) {
    // Mantido para reativação futura do "Resumo da reserva".
    // const [expanded, setExpanded] = useState(false);
    // const reactId = useId();
    // const toggleId = `resumo-reserva-${item.id}-${reactId}`;
    // const panelId = `painel-reserva-${item.id}-${reactId}`;
    // const waves = getOwnerReservationWaveSummaryCounts(item);
    // const L = OWNER_RESERVATION_WAVES_LIMIT_PER_DAY;
    // const wavesFull = isOwnerReservationWavesFullyScheduled(item);

    return (
        <ReservationCard $surface={surface}>
            <h3>
                {item.contractId != null
                    ? `Reserva do contrato #${item.contractId}`
                    : `Reserva #${item.id}`}
            </h3>
            <dl>
                <dt>Check-in</dt>
                <dd>{moment(item.checkIn).format('DD/MM/YYYY')}</dd>
                <dt>Check-out</dt>
                <dd>{moment(item.checkOut).format('DD/MM/YYYY')}</dd>
            </dl>
            {/* Bloco "Resumo da reserva" removido temporariamente da UI.
                Código mantido acima para futura reativação quando necessário. */}
            {item.token ? (
                <ReservationAccessLink
                    to={`/reserva?token=${encodeURIComponent(item.token)}`}
                    onClick={() => onClose()}
                >
                    Acessar reserva
                </ReservationAccessLink>
            ) : null}
        </ReservationCard>
    );
}

export const VerReservasHeaderTrigger: React.FC<{
    onClick: () => void;
    compact?: boolean;
}> = ({ onClick, compact }) => (
    <VerReservasHeaderButton
        type="button"
        onClick={onClick}
        aria-haspopup="dialog"
        aria-label="Ver suas reservas de hospitalidade"
        $compact={compact}
    >
        <LuCalendarDays size={compact ? 22 : 20} aria-hidden />
        {!compact && <span>Ver reservas</span>}
    </VerReservasHeaderButton>
);

export const OwnerReservationsPanel: React.FC<OwnerReservationsPanelProps> = ({
    open,
    onClose,
}) => {
    const isDesktop = useMediaQuery('(min-width: 1024px)');
    const { data, isPending, isError, error, refetch } = useHospitalityReservationList(open);

    const list = data ?? [];

    const retryControl = isDesktop ? (
        <ModalRetryButton type="button" onClick={() => void refetch()}>
            Tentar novamente
        </ModalRetryButton>
    ) : (
        <DrawerRetryButton type="button" onClick={() => void refetch()}>
            Tentar novamente
        </DrawerRetryButton>
    );

    const content = (
        <>
            {isPending && (
                <CenteredState className={!isDesktop ? 'inverse' : undefined}>
                    <Spinner className={!isDesktop ? 'inverse' : undefined} aria-hidden />
                    <span>Carregando reservas…</span>
                </CenteredState>
            )}
            {!isPending && isError && (
                <CenteredState className={!isDesktop ? 'inverse' : undefined}>
                    <p>{error instanceof Error ? error.message : 'Erro ao carregar reservas.'}</p>
                    {retryControl}
                </CenteredState>
            )}
            {!isPending && !isError && list.length === 0 && (
                <CenteredState className={!isDesktop ? 'inverse' : undefined}>
                    <p>Nenhuma reserva encontrada com os filtros atuais.</p>
                </CenteredState>
            )}
            {!isPending && !isError && list.length > 0 && (
                <PanelScroll>
                    {list.map(item => (
                        <OwnerReservationListCard
                            key={item.id}
                            item={item}
                            surface={isDesktop ? 'light' : 'dark'}
                            onClose={onClose}
                        />
                    ))}
                </PanelScroll>
            )}
        </>
    );

    if (!open) return null;

    if (isDesktop) {
        return (
            <Modal
                showModal={open}
                setShowModal={() => onClose()}
                title="Suas reservas"
                stylesChildren={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                    maxHeight: 'min(70vh, 36rem)',
                }}
            >
                {content}
            </Modal>
        );
    }

    return (
        <Drawer
            isOpen={open}
            toggleDrawer={onClose}
            stylesDrawer={{
                marginTop: '4rem',
                maxHeight: '85vh',
                width: '100%',
                maxWidth: '100%',
                borderRadius: '1rem 1rem 0 0',
            }}
            drawerClassName="owner-reservations-drawer"
        >
            <DrawerHeader>
                <h2 id="owner-reservations-title">Suas reservas</h2>
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Fechar lista de reservas"
                >
                    <LuX size={22} aria-hidden="true" focusable="false" />
                </button>
            </DrawerHeader>
            <div role="region" aria-labelledby="owner-reservations-title">
                {content}
            </div>
        </Drawer>
    );
};
